import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../data/context/app-context';
import {
  GetHonorificBlockTimesDocument,
  HealthProviderItemDataFragment,
  HonorificBlockTimeItemDataFragment,
  UpdateHonorificBlockTimeItemDocument,
} from '../../../data/types/api/graphql';
import { FormError } from '../../../data/utils/form.utils';
import { AlertMessageType } from '../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../elements/button/button';
import { InputCheckboxController } from '../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../elements/form/controllers/input-field';
import { InputSelectController } from '../../../elements/form/controllers/input-select';
import { FormRow } from '../../../elements/form/form';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Message, MessageType } from '../../../elements/message/message';
import { Modal, ModalWidth } from '../../../elements/modal/modal';
import { ModalPrompt } from '../../../elements/modal/modal-prompt';
import { useHonorificBlockTimesForm } from '../form/use-honorific-block-times-form';

type HonorificBlockTimesModalProps = {
  healthProvider: HealthProviderItemDataFragment;
  honorificBlockTimeItemData: HonorificBlockTimeItemDataFragment;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

const blockMinutes = Array.from({ length: 24 }, (_, i) => (i + 1) * 5);

export function HonorificBlockTimesModal({
  healthProvider,
  honorificBlockTimeItemData,
  onSaved,
  onCancel,
  onClose,
}: HonorificBlockTimesModalProps) {
  const intl = useIntl();

  const [needsInitialSave, setNeedsInitialSave] = useState(false);

  const { form, formIsValid, setErrors } = useHonorificBlockTimesForm();
  const { showAlertMessage } = useAppContext();

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);
  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();
        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const [
    updateHonorificBlockTimesItem,
    { loading: updateHonorificBlockTimesItemLoading },
  ] = useMutation(UpdateHonorificBlockTimeItemDocument, {
    refetchQueries: [GetHonorificBlockTimesDocument],
  });

  const editAppointmentBeforeHours = form.watch('editAppointmentBeforeHours');

  useEffect(() => {
    if (honorificBlockTimeItemData) {
      const hasInvalidValues = honorificBlockTimeItemData.blockMinutes <= 0;

      // Track if we need initial save due to invalid values
      setNeedsInitialSave(hasInvalidValues);

      form.reset({
        blockMinutes:
          honorificBlockTimeItemData.blockMinutes <= 0
            ? 5
            : honorificBlockTimeItemData.blockMinutes,
        fallBackMinutes:
          honorificBlockTimeItemData.fallBackMinutes <= 0
            ? 5
            : honorificBlockTimeItemData.fallBackMinutes,
        editAppointmentBeforeHours:
          honorificBlockTimeItemData.editAppointmentBeforeHours ?? 4,
        snap: honorificBlockTimeItemData.snap ?? false,
      });

      const errors: FormError[] = [];
      if (honorificBlockTimeItemData.blockMinutes <= 0) {
        errors.push({
          fieldName: 'blockMinutes',
          message: 'generic',
        });
      }

      setErrors(errors);
    }
  }, [honorificBlockTimeItemData, form, setErrors]);

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await updateHonorificBlockTimesItem({
          variables: {
            snap: values.snap === true,
            honorific: honorificBlockTimeItemData.honorific,
            blockMinutes: Number(values.blockMinutes),
            fallBackMinutes: Number(values.fallBackMinutes),
            editAppointmentBeforeHours: Number(
              values.editAppointmentBeforeHours
            ),
            healthProviderId: healthProvider.id ?? '',
          },
        });
        if (result.data?.healthProviderHonorificBlockTimeMutate?.boolean) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'admin.honorific-block-times.update.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          setErrors(
            result.data?.healthProviderHonorificBlockTimeMutate?.errors
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    form,
    healthProvider.id,
    honorificBlockTimeItemData.honorific,
    intl,
    onClose,
    onSaved,
    setErrors,
    showAlertMessage,
    updateHonorificBlockTimesItem,
  ]);

  return (
    <>
      <Modal
        className="honorific-block-times-modal"
        testId="honorific-block-times-modal"
        loading={false}
        title={
          <CustomFormattedMessage
            id={`common.honorific.${kebabCase(
              honorificBlockTimeItemData.honorific
            )}`}
          />
        }
        width={ModalWidth.SMALL}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={
                updateHonorificBlockTimesItemLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.NONE
              }
              disabled={
                !formIsValid ||
                (!needsInitialSave && !form.formState.isDirty) ||
                updateHonorificBlockTimesItemLoading
              }
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="common.change" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <div className="description">
            <h4>
              <CustomFormattedMessage id="common.practice" />
            </h4>
            {healthProvider?.name ?? ''}
          </div>

          <FormRow>
            <InputSelectController<number>
              name="blockMinutes"
              label={
                <>
                  <CustomFormattedMessage id="admin.honorific-block-times.block-minutes" />
                  <small>*</small>
                </>
              }
              options={blockMinutes.map(option => ({
                key: option,
                title: `${option}`,
              }))}
            />
          </FormRow>
          <FormRow>
            <InputSelectController<number>
              name="fallBackMinutes"
              label={
                <>
                  <CustomFormattedMessage id="admin.honorific-block-times.fallback-block-minutes" />
                  <small>*</small>
                </>
              }
              options={blockMinutes.map(option => ({
                key: option,
                title: `${option}`,
              }))}
            />
          </FormRow>
          <FormRow>
            <InputFieldController
              name="editAppointmentBeforeHours"
              max={8766}
              type="number"
              label={
                <>
                  <CustomFormattedMessage id="admin.honorific-block-times.edit-appointment-before-hours" />
                  <small>*</small>
                </>
              }
            />
          </FormRow>

          {editAppointmentBeforeHours.toString() === '0' && (
            <Message
              type={MessageType.INFO}
              message={
                <CustomFormattedMessage id="admin.honorific-block-times.edit-appointment-before-hours.info" />
              }
            />
          )}
          <FormRow>
            <InputCheckboxController<boolean>
              name="snap"
              options={[
                {
                  key: true,
                  title: (
                    <CustomFormattedMessage id="admin.honorific-block-times.snap-placeholder" />
                  ),
                },
              ]}
            />
          </FormRow>
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
